import { DEF, I_ADDRS, Q_ADDRS } from "./library";
import { COILS } from "./ladder-utils";
import type { Project } from "./types";

export type Level = "error" | "warning" | "info";

export interface Issue {
  id: string;
  level: Level;
  msg: string;
  deviceId?: string;
  netId?: string;
  addr?: string;
}

/** static project check — used for the error bar and the pre-RUN check */
export function diagnose(project: Project): Issue[] {
  const out: Issue[] = [];
  const push = (i: Issue) => out.push(i);

  const wired = new Set<string>();
  for (const w of project.wires) {
    wired.add(w.from);
    wired.add(w.to);
  }

  /* wires pointing at removed devices / terminals */
  for (const w of project.wires) {
    for (const end of [w.from, w.to]) {
      const [id, term] = end.split(":");
      const d = project.devices.find((x) => x.id === id);
      if (!d) {
        push({ id: `w-${w.id}-${end}`, level: "error", msg: `سلك غير مكتمل: الطرف ${end} غير موجود` });
        continue;
      }
      if (!DEF[d.type]?.terminals.some((t) => t.id === term))
        push({
          id: `wt-${w.id}-${end}`,
          level: "error",
          msg: `Terminal غير متصل: ${d.label} / ${term}`,
          deviceId: d.id,
        });
    }
  }

  /* power source */
  const hasSource = project.devices.some((d) => DEF[d.type]?.sources?.length);
  if (project.devices.length && !hasSource)
    push({ id: "no-src", level: "error", msg: "مصدر الطاقة غير موجود في اللوحة" });

  const plc = project.devices.find((d) => d.type === "plc");
  if (project.devices.length && !plc)
    push({ id: "no-plc", level: "error", msg: "لا يوجد PLC في اللوحة" });

  /* ladder */
  const coilCount = new Map<string, number>();
  for (const net of project.networks) {
    for (const row of net.rows) {
      for (const cell of row) {
        if (cell.type === "EMPTY") continue;
        if (!cell.operand.trim() && cell.type !== "CMP")
          push({
            id: `op-${net.id}-${Math.random().toString(36).slice(2, 6)}`,
            level: "error",
            msg: `خطأ في Ladder: عنصر ${cell.type} بدون عنوان في ${net.title}`,
            netId: net.id,
          });
        if (["TON", "TOF", "TP", "TONR"].includes(cell.type) && !String(cell.preset ?? "").trim())
          push({
            id: `t-${net.id}-${cell.operand}`,
            level: "error",
            msg: `Timer بدون قيمة زمنية: ${cell.operand || cell.type}`,
            netId: net.id,
          });
        if (COILS.includes(cell.type) && cell.operand)
          coilCount.set(cell.operand, (coilCount.get(cell.operand) ?? 0) + 1);
      }
    }
  }
  for (const [addr, n] of coilCount)
    if (n > 1 && !addr.startsWith("M"))
      push({
        id: `dup-${addr}`,
        level: "error",
        msg: `${addr} مربوط بأكثر من Output — غير مسموح (${n} مرات)`,
        addr,
      });

  /* addresses used in the program but not wired on the PLC */
  if (plc) {
    const used = new Set<string>();
    for (const net of project.networks)
      for (const row of net.rows)
        for (const cell of row) if (cell.operand) used.add(cell.operand);

    for (const addr of used) {
      if (!I_ADDRS.includes(addr) && !Q_ADDRS.includes(addr)) continue;
      if (!wired.has(`${plc.id}:${addr}`))
        push({
          id: `nw-${addr}`,
          level: "warning",
          msg: `${addr} غير مربوط بأي قطعة`,
          deviceId: plc.id,
          addr,
        });
    }

    if (!wired.has(`${plc.id}:L+`) || !wired.has(`${plc.id}:M`))
      push({
        id: "plc-power",
        level: "error",
        msg: "تغذية الـPLC غير موصولة (L+ / M)",
        deviceId: plc.id,
      });
  }

  /* devices with a completely free coil */
  for (const d of project.devices) {
    const def = DEF[d.type];
    if (!def) continue;
    const free = def.terminals.filter((t) => !wired.has(`${d.id}:${t.id}`));
    if (def.terminals.length && free.length === def.terminals.length)
      push({
        id: `free-${d.id}`,
        level: "info",
        msg: `${d.label} غير موصولة بأي سلك`,
        deviceId: d.id,
      });
  }

  return out;
}

export function blocksRun(issues: Issue[]) {
  return issues.filter((i) => i.level === "error");
}
